// CalendarSettings.js - Inställningar för kalendermodulen
import React, { useState, useEffect, useContext } from 'react';
import { useNavigate } from 'react-router-dom';
import apiClient from '../../apiClient';
import { UserContext } from '../../UserContext';
import { isClinicLevelAdmin } from '../../utils/permissions';
import { toast } from 'react-toastify';
import ScheduleManager from '../Calendar/ScheduleManager';
import CalendarViewsManager from './CalendarViewsManager';
import './CalendarSettings.css';

function CalendarSettings() {
  const navigate = useNavigate();
  const { user } = useContext(UserContext);

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  // Inställningar
  const [settings, setSettings] = useState({
    sms_reminder_enabled: false,
    sms_reminder_hours: 24,
    sms_reminder_template_id: null,
    email_reminder_enabled: false,
    email_reminder_hours: 24,
    email_reminder_template_id: null,
    booking_confirmation_enabled: false,
    booking_confirmation_method: 'sms',
    booking_confirmation_sms_template_id: null,
    booking_confirmation_email_template_id: null,
    smtp_sender_email: '',
    smtp_password: '',            // skriv-bara: skickas bara om ifyllt
    smtp_server: '',
    smtp_port: '',
    ekopost_enabled: false,
    ekopost_sender_name: '',
    ekopost_sender_street: '',
    ekopost_sender_postal_code: '',
    ekopost_sender_city: '',
    ekopost_cost_center: '',
    fakturamottagare_email: '',
    utebliven_avgift: 200,
    uteblivet_avgiftsfri_under_alder: '',
    uteblivet_avgiftsfri_fran_alder: '',
    opening_hours: null,
    default_appointment_duration: 30,
    booking_interval_minutes: 15,
    allow_online_booking: false,
    release_enabled: false,
    release_hours: 24,
    release_to_type_ids: null   // null = alla typer
  });

  // Bokningstyper för frisläppnings-val
  const [appointmentTypes, setAppointmentTypes] = useState([]);

  // Schemahantering (arbetstider) - flyttad hit från kalender-toolbaren (Spår J);
  // hela sidan är admin-gated, så bara admin når modalen.
  const [showScheduleManager, setShowScheduleManager] = useState(false);

  // Om ett SMTP-lösenord redan är sparat (backend returnerar aldrig värdet)
  const [smtpPasswordSet, setSmtpPasswordSet] = useState(false);
  const [testEmailSending, setTestEmailSending] = useState(false);

  // Alternativ
  const [smsTemplates, setSmsTemplates] = useState([]);
  const [emailTemplates, setEmailTemplates] = useState([]);
  const [confirmationEmailTemplates, setConfirmationEmailTemplates] = useState([]);
  const [reminderOptions, setReminderOptions] = useState([]);
  const [intervalOptions, setIntervalOptions] = useState([]);

  // Röda dagar / Helgdagar
  const [publicHolidays, setPublicHolidays] = useState([]);
  const [customHolidays, setCustomHolidays] = useState([]);
  const [showAddHoliday, setShowAddHoliday] = useState(false);
  const [newHoliday, setNewHoliday] = useState({ name: '', date: '' });
  const [holidaysLoading, setHolidaysLoading] = useState(false);
  const [holidaysError, setHolidaysError] = useState(null);

  // Hämta helgdagar
  const fetchHolidays = async () => {
    try {
      setHolidaysLoading(true);
      setHolidaysError(null);
      const res = await apiClient.get('/api/calendar/holidays');
      setPublicHolidays(res.data.public_holidays || []);
      setCustomHolidays(res.data.custom_holidays || []);
    } catch (err) {
      console.error('Error fetching holidays:', err);
      setHolidaysError('Kunde inte hämta helgdagar. Kontrollera att servern är igång.');
    } finally {
      setHolidaysLoading(false);
    }
  };

  // Hämta inställningar och alternativ
  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        setError(null);

        const [settingsRes, optionsRes] = await Promise.all([
          apiClient.get('/api/calendar/settings'),
          apiClient.get('/api/calendar/settings/reminder-options')
        ]);

        setSettings({
          sms_reminder_enabled: settingsRes.data.sms_reminder_enabled || false,
          sms_reminder_hours: settingsRes.data.sms_reminder_hours || 24,
          sms_reminder_template_id: settingsRes.data.sms_reminder_template_id || '',
          email_reminder_enabled: settingsRes.data.email_reminder_enabled || false,
          email_reminder_hours: settingsRes.data.email_reminder_hours || 24,
          email_reminder_template_id: settingsRes.data.email_reminder_template_id || '',
          booking_confirmation_enabled: settingsRes.data.booking_confirmation_enabled || false,
          booking_confirmation_method: settingsRes.data.booking_confirmation_method || 'sms',
          booking_confirmation_sms_template_id: settingsRes.data.booking_confirmation_sms_template_id || '',
          booking_confirmation_email_template_id: settingsRes.data.booking_confirmation_email_template_id || '',
          smtp_sender_email: settingsRes.data.smtp_sender_email || '',
          smtp_password: '',
          smtp_server: settingsRes.data.smtp_server || '',
          smtp_port: settingsRes.data.smtp_port || '',
          ekopost_enabled: settingsRes.data.ekopost_enabled || false,
          ekopost_sender_name: settingsRes.data.ekopost_sender_name || '',
          ekopost_sender_street: settingsRes.data.ekopost_sender_street || '',
          ekopost_sender_postal_code: settingsRes.data.ekopost_sender_postal_code || '',
          ekopost_sender_city: settingsRes.data.ekopost_sender_city || '',
          ekopost_cost_center: settingsRes.data.ekopost_cost_center || '',
          fakturamottagare_email: settingsRes.data.fakturamottagare_email || '',
          utebliven_avgift: settingsRes.data.utebliven_avgift ?? 200,
          uteblivet_avgiftsfri_under_alder: settingsRes.data.uteblivet_avgiftsfri_under_alder ?? '',
          uteblivet_avgiftsfri_fran_alder: settingsRes.data.uteblivet_avgiftsfri_fran_alder ?? '',
          opening_hours: settingsRes.data.opening_hours || null,
          default_appointment_duration: settingsRes.data.default_appointment_duration || 30,
          booking_interval_minutes: settingsRes.data.booking_interval_minutes || 15,
          allow_online_booking: settingsRes.data.allow_online_booking || false,
          release_enabled: settingsRes.data.release_enabled || false,
          release_hours: settingsRes.data.release_hours || 24,
          release_to_type_ids: settingsRes.data.release_to_type_ids || null
        });
        setSmtpPasswordSet(Boolean(settingsRes.data.smtp_password_set));

        // Bokningstyper (för frisläppnings-val) — misslyckas tyst
        try {
          const typesRes = await apiClient.get('/api/calendar/appointment-types');
          setAppointmentTypes(typesRes.data.appointment_types || typesRes.data || []);
        } catch (e) { /* inga typer -> tom lista */ }

        setSmsTemplates(settingsRes.data.available_sms_templates || []);
        setEmailTemplates(settingsRes.data.available_email_templates || []);
        setConfirmationEmailTemplates(settingsRes.data.available_confirmation_email_templates || []);
        setReminderOptions(optionsRes.data.reminder_hours_options || []);
        setIntervalOptions(optionsRes.data.booking_interval_options || []);

        // Hämta helgdagar
        await fetchHolidays();

      } catch (err) {
        console.error('Error fetching calendar settings:', err);
        setError('Kunde inte hämta inställningar');
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, []);

  // Spara inställningar
  const handleSave = async () => {
    try {
      setSaving(true);
      setError(null);

      await apiClient.put('/api/calendar/settings', settings);
      toast.success('Inställningar sparade');

      // Om ett lösenord angavs är det nu sparat - rensa fältet och markera som satt
      if (settings.smtp_password) {
        setSmtpPasswordSet(true);
        setSettings(prev => ({ ...prev, smtp_password: '' }));
      }
    } catch (err) {
      console.error('Error saving settings:', err);
      toast.error(err.response?.data?.msg || 'Kunde inte spara inställningar');
    } finally {
      setSaving(false);
    }
  };

  // Skicka testmejl med klinikens konfigurerade avsändare
  const handleSendTestEmail = async () => {
    // Spara först så att ev. nyss inmatade uppgifter finns i DB
    if (settings.smtp_sender_email && (settings.smtp_password || smtpPasswordSet)) {
      try {
        setTestEmailSending(true);
        await apiClient.put('/api/calendar/settings', settings);
        if (settings.smtp_password) {
          setSmtpPasswordSet(true);
          setSettings(prev => ({ ...prev, smtp_password: '' }));
        }
        const res = await apiClient.post('/api/calendar/settings/test-email', {});
        toast.success(res.data?.msg || 'Testmejl skickat');
      } catch (err) {
        console.error('Error sending test email:', err);
        toast.error(err.response?.data?.msg || 'Kunde inte skicka testmejl');
      } finally {
        setTestEmailSending(false);
      }
    } else {
      toast.error('Fyll i avsändaradress och lösenord först');
    }
  };

  // Uppdatera ett fält
  const updateSetting = (field, value) => {
    setSettings(prev => ({ ...prev, [field]: value }));
  };

  // Toggle helgdag
  const toggleHoliday = async (holidayCode, enabled) => {
    try {
      await apiClient.post('/api/calendar/holidays/toggle', {
        holiday_code: holidayCode,
        enabled: enabled
      });

      // Uppdatera lokal state
      setPublicHolidays(prev =>
        prev.map(h =>
          h.code === holidayCode ? { ...h, enabled: enabled } : h
        )
      );

      toast.success(enabled ? 'Helgdag aktiverad' : 'Helgdag inaktiverad');
    } catch (err) {
      console.error('Error toggling holiday:', err);
      toast.error('Kunde inte uppdatera helgdag');
    }
  };

  // Skapa egen stängningsdag
  const createCustomHoliday = async () => {
    if (!newHoliday.name || !newHoliday.date) {
      toast.error('Ange namn och datum');
      return;
    }

    try {
      const res = await apiClient.post('/api/calendar/holidays', {
        name: newHoliday.name,
        date: newHoliday.date,
        blocks_calendar: true
      });

      setCustomHolidays(prev => [...prev, res.data.holiday]);
      setNewHoliday({ name: '', date: '' });
      setShowAddHoliday(false);
      toast.success('Stängningsdag skapad');
    } catch (err) {
      console.error('Error creating holiday:', err);
      toast.error('Kunde inte skapa stängningsdag');
    }
  };

  // Ta bort egen stängningsdag
  const deleteCustomHoliday = async (holidayId) => {
    if (!window.confirm('Är du säker på att du vill ta bort denna stängningsdag?')) {
      return;
    }

    try {
      await apiClient.delete(`/api/calendar/holidays/${holidayId}`);
      setCustomHolidays(prev => prev.filter(h => h.holiday_id !== holidayId));
      toast.success('Stängningsdag borttagen');
    } catch (err) {
      console.error('Error deleting holiday:', err);
      toast.error('Kunde inte ta bort stängningsdag');
    }
  };

  if (!isClinicLevelAdmin(user)) {
    return (
      <div className="calendar-settings-container">
        <div className="access-denied">
          <i className="fas fa-lock"></i>
          <h2>Åtkomst nekad</h2>
          <p>Endast administratörer kan ändra kalenderinställningar.</p>
        </div>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="calendar-settings-container">
        <div className="loading">
          <i className="fas fa-spinner fa-spin"></i> Laddar inställningar...
        </div>
      </div>
    );
  }

  return (
    <div className="calendar-settings-container">
      <div className="settings-header">
        <button className="back-btn" onClick={() => navigate(-1)}>
          <i className="fas fa-arrow-left"></i>
        </button>
        <h1><i className="fas fa-cog"></i> Kalenderinställningar</h1>
      </div>

      {error && <div className="error-message">{error}</div>}

      <div className="settings-content">
        {/* SMS-påminnelser */}
        <section className="settings-section">
          <div className="section-header">
            <h2><i className="fas fa-sms"></i> SMS-påminnelser</h2>
            <p>Skicka automatiska påminnelser till patienter före deras bokade tid.</p>
          </div>

          <div className="setting-row">
            <div className="setting-info">
              <label>Aktivera SMS-påminnelser</label>
              <span className="setting-description">
                När aktiverat skickas automatiska SMS till patienter
              </span>
            </div>
            <label className="toggle-switch">
              <input
                type="checkbox"
                checked={settings.sms_reminder_enabled}
                onChange={(e) => updateSetting('sms_reminder_enabled', e.target.checked)}
              />
              <span className="toggle-slider"></span>
            </label>
          </div>

          {settings.sms_reminder_enabled && (
            <>
              <div className="setting-row">
                <div className="setting-info">
                  <label>Tid före bokning</label>
                  <span className="setting-description">
                    Hur långt i förväg påminnelsen ska skickas
                  </span>
                </div>
                <select
                  value={settings.sms_reminder_hours}
                  onChange={(e) => updateSetting('sms_reminder_hours', parseInt(e.target.value))}
                >
                  {reminderOptions.map(opt => (
                    <option key={opt.value} value={opt.value}>{opt.label}</option>
                  ))}
                </select>
              </div>

              <div className="setting-row">
                <div className="setting-info">
                  <label>SMS-mall</label>
                  <span className="setting-description">
                    Välj vilken mall som ska användas för påminnelser
                  </span>
                </div>
                <select
                  value={settings.sms_reminder_template_id || ''}
                  onChange={(e) => updateSetting('sms_reminder_template_id', e.target.value || null)}
                >
                  <option value="">-- Välj mall --</option>
                  {smsTemplates.map(t => (
                    <option key={t.template_id} value={t.template_id}>{t.template_name}</option>
                  ))}
                </select>
              </div>

              {smsTemplates.length === 0 && (
                <div className="setting-warning">
                  <i className="fas fa-exclamation-triangle"></i>
                  <span>
                    Du har inga SMS-mallar.
                    <button onClick={() => navigate('/sms-mallar/create')} className="link-btn">
                      Skapa en mall
                    </button>
                  </span>
                </div>
              )}

              {settings.sms_reminder_enabled && !settings.sms_reminder_template_id && (
                <div className="setting-warning">
                  <i className="fas fa-exclamation-triangle"></i>
                  <span>Välj en SMS-mall för att aktivera påminnelser</span>
                </div>
              )}
            </>
          )}
        </section>

        {/* Email-påminnelser */}
        <section className="settings-section">
          <div className="section-header">
            <h2><i className="fas fa-envelope"></i> Email-påminnelser</h2>
            <p>Skicka automatiska email-påminnelser till patienter före deras bokade tid.</p>
          </div>

          <div className="setting-row">
            <div className="setting-info">
              <label>Aktivera email-påminnelser</label>
              <span className="setting-description">
                När aktiverat skickas automatiska email till patienter
              </span>
            </div>
            <label className="toggle-switch">
              <input
                type="checkbox"
                checked={settings.email_reminder_enabled}
                onChange={(e) => updateSetting('email_reminder_enabled', e.target.checked)}
              />
              <span className="toggle-slider"></span>
            </label>
          </div>

          {settings.email_reminder_enabled && (
            <>
              <div className="setting-row">
                <div className="setting-info">
                  <label>Tid före bokning</label>
                  <span className="setting-description">
                    Hur långt i förväg påminnelsen ska skickas
                  </span>
                </div>
                <select
                  value={settings.email_reminder_hours}
                  onChange={(e) => updateSetting('email_reminder_hours', parseInt(e.target.value))}
                >
                  {reminderOptions.map(opt => (
                    <option key={opt.value} value={opt.value}>{opt.label}</option>
                  ))}
                </select>
              </div>

              <div className="setting-row">
                <div className="setting-info">
                  <label>Email-mall</label>
                  <span className="setting-description">
                    Välj vilken mall som ska användas för påminnelser
                  </span>
                </div>
                <select
                  value={settings.email_reminder_template_id || ''}
                  onChange={(e) => updateSetting('email_reminder_template_id', e.target.value || null)}
                >
                  <option value="">-- Välj mall --</option>
                  {emailTemplates.map(t => (
                    <option key={t.template_id} value={t.template_id}>{t.template_name}</option>
                  ))}
                </select>
              </div>

              {emailTemplates.length === 0 && (
                <div className="setting-warning">
                  <i className="fas fa-exclamation-triangle"></i>
                  <span>
                    Du har inga email-mallar. Skapa en kallelsemall under Kallelse-mallar.
                  </span>
                </div>
              )}

              {settings.email_reminder_enabled && !settings.email_reminder_template_id && (
                <div className="setting-warning">
                  <i className="fas fa-exclamation-triangle"></i>
                  <span>Välj en email-mall för att aktivera påminnelser</span>
                </div>
              )}
            </>
          )}
        </section>

        {/* Bokningsbekräftelse */}
        <section className="settings-section">
          <div className="section-header">
            <h2><i className="fas fa-check-circle"></i> Bokningsbekräftelse</h2>
            <p>
              Bekräftelse till patienten direkt när en bokning skapas. Det här är klinikens förval —
              i bokningsrutan kan man välja för varje bokning, och bokningstyper kan ha egen mall
              och eget förval (Inställningar → Bokningstyper).
            </p>
          </div>

          <div className="setting-row">
            <div className="setting-info">
              <label>Förvalt: skicka bekräftelse vid ny bokning</label>
              <span className="setting-description">
                Om rutan "Skicka bokningsbekräftelse" är ikryssad från början
              </span>
            </div>
            <label className="toggle-switch">
              <input
                type="checkbox"
                checked={settings.booking_confirmation_enabled}
                onChange={(e) => updateSetting('booking_confirmation_enabled', e.target.checked)}
              />
              <span className="toggle-slider"></span>
            </label>
          </div>

          <div className="setting-row">
            <div className="setting-info">
              <label>Standardkanal</label>
              <span className="setting-description">
                Saknar patienten kontaktuppgift för kanalen används den andra
              </span>
            </div>
            <select
              value={settings.booking_confirmation_method}
              onChange={(e) => updateSetting('booking_confirmation_method', e.target.value)}
            >
              <option value="sms">SMS</option>
              <option value="email">E-post</option>
            </select>
          </div>

          <div className="setting-row">
            <div className="setting-info">
              <label>SMS-mall</label>
              <span className="setting-description">
                Används för SMS-bekräftelser när bokningstypen saknar egen mall
              </span>
            </div>
            <select
              value={settings.booking_confirmation_sms_template_id || ''}
              onChange={(e) => updateSetting('booking_confirmation_sms_template_id', e.target.value || null)}
            >
              <option value="">-- Ingen SMS-mall --</option>
              {smsTemplates.map(t => (
                <option key={t.template_id} value={t.template_id}>{t.template_name}</option>
              ))}
            </select>
          </div>

          <div className="setting-row">
            <div className="setting-info">
              <label>E-postmall</label>
              <span className="setting-description">
                Används för e-postbekräftelser när bokningstypen saknar egen mall
              </span>
            </div>
            <select
              value={settings.booking_confirmation_email_template_id || ''}
              onChange={(e) => updateSetting('booking_confirmation_email_template_id', e.target.value || null)}
            >
              <option value="">-- Ingen e-postmall --</option>
              {confirmationEmailTemplates.map(t => (
                <option key={t.template_id} value={t.template_id}>{t.template_name}</option>
              ))}
            </select>
          </div>

          {smsTemplates.length === 0 && (
            <div className="setting-warning">
              <i className="fas fa-exclamation-triangle"></i>
              <span>
                Du har inga SMS-mallar.
                <button onClick={() => navigate('/sms-mallar/create')} className="link-btn">
                  Skapa en mall
                </button>
              </span>
            </div>
          )}

          {confirmationEmailTemplates.length === 0 && (
            <div className="setting-warning">
              <i className="fas fa-exclamation-triangle"></i>
              <span>
                Du har inga e-postmallar. Skapa en kallelsemall under Kallelse-mallar.
              </span>
            </div>
          )}

          {settings.booking_confirmation_enabled && !(settings.booking_confirmation_method === 'sms'
            ? settings.booking_confirmation_sms_template_id
            : settings.booking_confirmation_email_template_id) && (
            <div className="setting-warning">
              <i className="fas fa-exclamation-triangle"></i>
              <span>
                Välj en {settings.booking_confirmation_method === 'sms' ? 'SMS-mall' : 'e-postmall'} för
                standardkanalen för att skicka bekräftelse som förval
              </span>
            </div>
          )}

          <div className="setting-row">
            <div className="setting-info">
              <span className="setting-description">
                <i className="fas fa-info-circle"></i>{' '}
                Ingen bekräftelse skickas för en bokning som redan har startat. Går ett SMS inte fram
                skickas e-post i stället (om patienten har e-post och det finns en e-postmall).
                Bokningen skapas alltid, även om utskicket misslyckas.
              </span>
            </div>
          </div>
        </section>

        {/* E-postavsändare (SMTP) */}
        <section className="settings-section">
          <div className="section-header">
            <h2><i className="fas fa-at"></i> E-postavsändare</h2>
            <p>Adress och lösenord som klinikens patient-mail (bekräftelser, påminnelser, kallelser) skickas från.</p>
          </div>

          <div className="setting-row">
            <div className="setting-info">
              <label>Avsändaradress</label>
              <span className="setting-description">
                E-postadressen mailen skickas från (används även som användarnamn vid inloggning)
              </span>
            </div>
            <input
              type="email"
              placeholder="t.ex. kliniken@dinklinik.se"
              value={settings.smtp_sender_email}
              onChange={(e) => updateSetting('smtp_sender_email', e.target.value)}
            />
          </div>

          <div className="setting-row">
            <div className="setting-info">
              <label>Lösenord</label>
              <span className="setting-description">
                {smtpPasswordSet
                  ? 'Ett lösenord är sparat. Lämna tomt för att behålla det, eller skriv ett nytt för att byta.'
                  : 'Lösenordet lagras krypterat och visas aldrig igen.'}
              </span>
            </div>
            <input
              type="password"
              autoComplete="new-password"
              placeholder={smtpPasswordSet ? '•••••••• (sparat)' : 'Lösenord'}
              value={settings.smtp_password}
              onChange={(e) => updateSetting('smtp_password', e.target.value)}
            />
          </div>

          <div className="setting-row">
            <div className="setting-info">
              <label>SMTP-server</label>
              <span className="setting-description">Lämna tomt för standard (smtp.office365.com)</span>
            </div>
            <input
              type="text"
              placeholder="smtp.office365.com"
              value={settings.smtp_server}
              onChange={(e) => updateSetting('smtp_server', e.target.value)}
            />
          </div>

          <div className="setting-row">
            <div className="setting-info">
              <label>Port</label>
              <span className="setting-description">Lämna tomt för standard (587)</span>
            </div>
            <input
              type="number"
              placeholder="587"
              value={settings.smtp_port}
              onChange={(e) => updateSetting('smtp_port', e.target.value)}
            />
          </div>

          <div className="setting-row">
            <div className="setting-info">
              <label>Testa avsändaren</label>
              <span className="setting-description">
                Sparar och skickar ett testmejl till din egen adress
              </span>
            </div>
            <button
              type="button"
              className="link-btn"
              onClick={handleSendTestEmail}
              disabled={testEmailSending}
            >
              {testEmailSending ? 'Skickar…' : 'Skicka testmejl'}
            </button>
          </div>
        </section>

        {/* Brevutskick (Ekopost) */}
        <section className="settings-section">
          <div className="section-header">
            <h2><i className="fas fa-envelope"></i> Brevutskick (Ekopost)</h2>
            <p>Skicka kallelser som fysiska brev. API-nyckel konfigureras på servern (backend/.env).</p>
          </div>

          <div className="setting-row">
            <div className="setting-info">
              <label>Aktivera brevutskick</label>
              <span className="setting-description">
                Visar kanalen "Brev" i kallelsedialogen. Kräver ifylld returadress nedan.
              </span>
            </div>
            <label className="switch">
              <input
                type="checkbox"
                checked={settings.ekopost_enabled}
                onChange={(e) => updateSetting('ekopost_enabled', e.target.checked)}
              />
              <span className="slider"></span>
            </label>
          </div>

          {settings.ekopost_enabled && (
            <>
              <div className="setting-row">
                <div className="setting-info">
                  <label>Returadress – avsändarnamn</label>
                  <span className="setting-description">Klinikens namn på kuvertet</span>
                </div>
                <input
                  type="text"
                  placeholder="t.ex. Apex Rehab"
                  value={settings.ekopost_sender_name}
                  onChange={(e) => updateSetting('ekopost_sender_name', e.target.value)}
                />
              </div>
              <div className="setting-row">
                <div className="setting-info">
                  <label>Returadress – gata</label>
                </div>
                <input
                  type="text"
                  placeholder="Gatuadress"
                  value={settings.ekopost_sender_street}
                  onChange={(e) => updateSetting('ekopost_sender_street', e.target.value)}
                />
              </div>
              <div className="setting-row">
                <div className="setting-info">
                  <label>Returadress – postnummer</label>
                </div>
                <input
                  type="text"
                  placeholder="123 45"
                  value={settings.ekopost_sender_postal_code}
                  onChange={(e) => updateSetting('ekopost_sender_postal_code', e.target.value)}
                />
              </div>
              <div className="setting-row">
                <div className="setting-info">
                  <label>Returadress – ort</label>
                </div>
                <input
                  type="text"
                  placeholder="Stockholm"
                  value={settings.ekopost_sender_city}
                  onChange={(e) => updateSetting('ekopost_sender_city', e.target.value)}
                />
              </div>
              <div className="setting-row">
                <div className="setting-info">
                  <label>Kostnadsställe (valfritt)</label>
                  <span className="setting-description">Skickas till Ekopost för redovisning</span>
                </div>
                <input
                  type="text"
                  placeholder="t.ex. KALLELSER"
                  value={settings.ekopost_cost_center}
                  onChange={(e) => updateSetting('ekopost_cost_center', e.target.value)}
                />
              </div>
            </>
          )}
        </section>

        {/* Uteblivna besök (Spår D+E) */}
        <section className="settings-section">
          <div className="section-header">
            <h2><i className="fas fa-user-slash"></i> Uteblivna besök</h2>
            <p>
              När en bokning registreras som uteblivet läggs avgiften i dagskassan
              (betalmetod Uteblivet) och ett fakturaunderlag mailas automatiskt till
              mottagaren nedan. Underlaget innehåller patientens personnummer.
            </p>
          </div>

          <div className="setting-row">
            <div className="setting-info">
              <label>Fakturamottagare (e-post)</label>
              <span className="setting-description">
                Den som fakturerar patienten, t.ex. klinikens ekonomiansvariga.
                Lämna tomt för att inte skicka fakturaunderlag.
              </span>
            </div>
            <input
              type="email"
              placeholder="t.ex. ekonomi@dinklinik.se"
              value={settings.fakturamottagare_email}
              onChange={(e) => updateSetting('fakturamottagare_email', e.target.value)}
            />
          </div>

          <div className="setting-row">
            <div className="setting-info">
              <label>Avgift vid uteblivet (kr)</label>
              <span className="setting-description">
                Fast belopp som registreras i kassan när ett besök markeras som uteblivet
              </span>
            </div>
            <input
              type="number"
              min="0"
              step="1"
              value={settings.utebliven_avgift}
              onChange={(e) => updateSetting('utebliven_avgift', e.target.value === '' ? '' : parseFloat(e.target.value))}
            />
          </div>

          <div className="setting-row">
            <div className="setting-info">
              <label>Avgiftsfritt uteblivet efter ålder</label>
              <span className="setting-description">
                Ålder på besöksdagen. Ingen avgift och inget fakturaunderlag. Tomt = inget undantag.
              </span>
            </div>
            <div className="uteblivet-alder-inputs">
              <label>
                Under
                <input
                  type="number"
                  min="0"
                  max="130"
                  step="1"
                  placeholder="–"
                  value={settings.uteblivet_avgiftsfri_under_alder}
                  onChange={(e) => updateSetting('uteblivet_avgiftsfri_under_alder', e.target.value === '' ? '' : parseInt(e.target.value, 10))}
                />
                år
              </label>
              <label>
                Från
                <input
                  type="number"
                  min="0"
                  max="130"
                  step="1"
                  placeholder="–"
                  value={settings.uteblivet_avgiftsfri_fran_alder}
                  onChange={(e) => updateSetting('uteblivet_avgiftsfri_fran_alder', e.target.value === '' ? '' : parseInt(e.target.value, 10))}
                />
                år
              </label>
              <button
                type="button"
                className="link-btn"
                onClick={() => {
                  updateSetting('uteblivet_avgiftsfri_under_alder', 20);
                  updateSetting('uteblivet_avgiftsfri_fran_alder', 85);
                }}
              >
                Region Skåne (under 20, från 85)
              </button>
            </div>
          </div>
        </section>

        {/* Arbetstider (schema) */}
        <section className="settings-section">
          <div className="section-header">
            <h2><i className="fas fa-clock"></i> Arbetstider</h2>
            <p>Hantera behandlarnas arbetstider (schema). Bokningar utanför arbetstid visas med varning.</p>
          </div>

          <div className="setting-row">
            <div className="setting-info">
              <label>Behandlarscheman</label>
              <span className="setting-description">
                Ange arbetsdagar och tider per behandlare
              </span>
            </div>
            <button
              type="button"
              className="link-btn"
              onClick={() => setShowScheduleManager(true)}
            >
              <i className="fas fa-clock"></i> Hantera arbetstider
            </button>
          </div>
        </section>

        {/* Sparade behandlarvyer för dagvyn (Spår K2) */}
        <CalendarViewsManager />

        {/* Bokningsinställningar */}
        <section className="settings-section">
          <div className="section-header">
            <h2><i className="fas fa-calendar-alt"></i> Bokningsinställningar</h2>
            <p>Konfigurera hur bokningar fungerar i kalendern.</p>
          </div>

          <div className="setting-row">
            <div className="setting-info">
              <label>Bokningsintervall</label>
              <span className="setting-description">
                Minsta tidsintervall för bokningar (t.ex. var 15:e minut)
              </span>
            </div>
            <select
              value={settings.booking_interval_minutes}
              onChange={(e) => updateSetting('booking_interval_minutes', parseInt(e.target.value))}
            >
              {intervalOptions.map(opt => (
                <option key={opt.value} value={opt.value}>{opt.label}</option>
              ))}
            </select>
          </div>

          <div className="setting-row">
            <div className="setting-info">
              <label>Standard bokningslängd</label>
              <span className="setting-description">
                Standardlängd för nya bokningar (minuter)
              </span>
            </div>
            <input
              type="number"
              min="5"
              max="480"
              step="5"
              value={settings.default_appointment_duration}
              onChange={(e) => updateSetting('default_appointment_duration', parseInt(e.target.value))}
            />
          </div>
        </section>

        {/* Onlinebokning (framtida) */}
        <section className="settings-section disabled-section">
          <div className="section-header">
            <h2><i className="fas fa-globe"></i> Onlinebokning</h2>
            <span className="coming-soon">Kommer snart</span>
          </div>

          <div className="setting-row">
            <div className="setting-info">
              <label>Tillåt onlinebokning</label>
              <span className="setting-description">
                Låt patienter boka tider själva via Bokningsappen
              </span>
            </div>
            <label className="toggle-switch">
              <input
                type="checkbox"
                checked={settings.allow_online_booking}
                onChange={(e) => updateSetting('allow_online_booking', e.target.checked)}
              />
              <span className="toggle-slider"></span>
            </label>
          </div>

          {settings.allow_online_booking && (
            <>
              <div className="setting-row">
                <div className="setting-info">
                  <label>Frisläpp obokade tider</label>
                  <span className="setting-description">
                    Öppna en obokad onlinetid för fler bokningstyper när det närmar sig,
                    så att tiden inte står tom.
                  </span>
                </div>
                <label className="toggle-switch">
                  <input
                    type="checkbox"
                    checked={settings.release_enabled}
                    onChange={(e) => updateSetting('release_enabled', e.target.checked)}
                  />
                  <span className="toggle-slider"></span>
                </label>
              </div>

              {settings.release_enabled && (
                <>
                  <div className="setting-row">
                    <div className="setting-info">
                      <label>Frisläpp inom</label>
                      <span className="setting-description">
                        Så här många timmar före tiden öppnas den för fler typer.
                      </span>
                    </div>
                    <select
                      value={settings.release_hours}
                      onChange={(e) => updateSetting('release_hours', parseInt(e.target.value))}
                    >
                      {[2, 4, 6, 12, 24, 48, 72].map(h => (
                        <option key={h} value={h}>{h} timmar</option>
                      ))}
                    </select>
                  </div>

                  <div className="setting-row">
                    <div className="setting-info">
                      <label>Öppna för bokningstyper</label>
                      <span className="setting-description">
                        Vilka typer som får ta en frisläppt tid. Inga val = alla typer.
                      </span>
                    </div>
                    <div className="release-type-list">
                      {appointmentTypes.length === 0 ? (
                        <span className="setting-description">Inga bokningstyper</span>
                      ) : appointmentTypes.map(t => {
                        const selected = settings.release_to_type_ids || [];
                        const checked = selected.includes(t.type_id);
                        return (
                          <label key={t.type_id} className="release-type-item">
                            <input
                              type="checkbox"
                              checked={checked}
                              onChange={(e) => {
                                let next = selected.filter(id => id !== t.type_id);
                                if (e.target.checked) next = [...next, t.type_id];
                                updateSetting('release_to_type_ids', next.length ? next : null);
                              }}
                            />
                            {t.name}
                          </label>
                        );
                      })}
                    </div>
                  </div>
                </>
              )}
            </>
          )}
        </section>

        {/* Röda dagar */}
        <section className="settings-section">
          <div className="section-header">
            <h2><i className="fas fa-calendar-times"></i> Röda dagar</h2>
            <p>Hantera helgdagar och stängningsdagar som blockerar alla kalendrar.</p>
          </div>

          {holidaysLoading ? (
            <div className="loading-inline">
              <i className="fas fa-spinner fa-spin"></i> Laddar helgdagar...
            </div>
          ) : holidaysError ? (
            <div className="holidays-error">
              <i className="fas fa-exclamation-triangle"></i>
              <span>{holidaysError}</span>
              <button onClick={fetchHolidays} className="retry-btn">
                <i className="fas fa-redo"></i> Försök igen
              </button>
            </div>
          ) : (
            <>
              {/* Svenska helgdagar */}
              <div className="holidays-section">
                <h3 className="holidays-subtitle">Svenska helgdagar</h3>
                <p className="holidays-description">
                  Aktivera de helgdagar som ska blockera bokningar i kalendern.
                </p>
                {publicHolidays.length === 0 ? (
                  <p className="no-holidays-message">
                    Inga helgdagar kunde laddas. Starta om backend-servern.
                  </p>
                ) : (
                <div className="holidays-list">
                  {publicHolidays.map(holiday => (
                    <div className="holiday-row" key={holiday.code}>
                      <label className="toggle-switch small">
                        <input
                          type="checkbox"
                          checked={holiday.enabled}
                          onChange={(e) => toggleHoliday(holiday.code, e.target.checked)}
                        />
                        <span className="toggle-slider"></span>
                      </label>
                      <span className="holiday-name">{holiday.name}</span>
                      <span className="holiday-date">{holiday.date_formatted}</span>
                    </div>
                  ))}
                </div>
                )}
              </div>

              {/* Egna stängningsdagar */}
              <div className="holidays-section custom-holidays">
                <div className="custom-holidays-header">
                  <div>
                    <h3 className="holidays-subtitle">Egna stängningsdagar</h3>
                    <p className="holidays-description">
                      Lägg till egna dagar då kliniken är stängd (t.ex. planeringsdagar).
                    </p>
                  </div>
                  <button
                    className="add-holiday-btn"
                    onClick={() => setShowAddHoliday(true)}
                  >
                    <i className="fas fa-plus"></i> Lägg till
                  </button>
                </div>

                {showAddHoliday && (
                  <div className="add-holiday-form">
                    <input
                      type="text"
                      placeholder="Namn (t.ex. Planeringsdag)"
                      value={newHoliday.name}
                      onChange={(e) => setNewHoliday(prev => ({ ...prev, name: e.target.value }))}
                    />
                    <input
                      type="date"
                      value={newHoliday.date}
                      onChange={(e) => setNewHoliday(prev => ({ ...prev, date: e.target.value }))}
                    />
                    <div className="add-holiday-actions">
                      <button className="save-holiday-btn" onClick={createCustomHoliday}>
                        <i className="fas fa-check"></i> Spara
                      </button>
                      <button
                        className="cancel-holiday-btn"
                        onClick={() => {
                          setShowAddHoliday(false);
                          setNewHoliday({ name: '', date: '' });
                        }}
                      >
                        Avbryt
                      </button>
                    </div>
                  </div>
                )}

                {customHolidays.length > 0 ? (
                  <div className="custom-holidays-list">
                    {customHolidays.map(holiday => (
                      <div className="custom-holiday-row" key={holiday.holiday_id}>
                        <div className="custom-holiday-info">
                          <span className="holiday-name">{holiday.name}</span>
                          <span className="holiday-date">
                            {new Date(holiday.specific_date).toLocaleDateString('sv-SE')}
                          </span>
                        </div>
                        <button
                          className="delete-holiday-btn"
                          onClick={() => deleteCustomHoliday(holiday.holiday_id)}
                          title="Ta bort"
                        >
                          <i className="fas fa-trash"></i>
                        </button>
                      </div>
                    ))}
                  </div>
                ) : (
                  !showAddHoliday && (
                    <p className="no-custom-holidays">
                      Inga egna stängningsdagar tillagda.
                    </p>
                  )
                )}
              </div>
            </>
          )}
        </section>
      </div>

      {/* Schemahanterings-modal (arbetstider) */}
      {showScheduleManager && (
        <ScheduleManager
          onClose={() => setShowScheduleManager(false)}
        />
      )}

      {/* Spara-knapp */}
      <div className="settings-footer">
        <button
          className="save-btn"
          onClick={handleSave}
          disabled={saving}
        >
          {saving ? (
            <>
              <i className="fas fa-spinner fa-spin"></i> Sparar...
            </>
          ) : (
            <>
              <i className="fas fa-save"></i> Spara inställningar
            </>
          )}
        </button>
      </div>
    </div>
  );
}

export default CalendarSettings;
